import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import { Box, Button, IconButton, Popover, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { MouseEvent, useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { getEmojiPickerData } from './emoji-picker-locale';
import { EmojiPickerPosition, emojiPickerPlacementByPosition } from './emoji-picker-position';

const addEmojiMessage = defineMessage({
  id: 'fragments.form.addEmoji',
  defaultMessage: 'Agregar emoji',
});

const emojiSearchPlaceholderMessage = defineMessage({
  id: 'fragments.form.emojiSearchPlaceholder',
  defaultMessage: 'Buscar',
});

const emojiPickerOkMessage = defineMessage({
  id: 'fragments.form.emojiPickerOk',
  defaultMessage: 'Aceptar',
});

/** Caret / selection range in the related text field when the picker opened. */
export type EmojiSelectionRange = {
  start: number;
  end: number;
};

export type EmojiPickerButtonProps = {
  /**
   * Called with the selected emoji and the caret range captured when the picker opened
   * so callers can insert at the caret instead of appending.
   */
  onSelect: (emoji: string, selection: EmojiSelectionRange) => void;
  /** Where the picker opens relative to the input. Defaults to `bottom`. */
  position?: EmojiPickerPosition;
};

/** Finds the text field input associated with an adornment emoji button. */
function findRelatedInput(anchor: HTMLElement | null) {
  return (
    anchor
      ?.closest('.MuiInputBase-root')
      ?.querySelector<HTMLInputElement | HTMLTextAreaElement>('textarea, input') ?? null
  );
}

/**
 * Reads the caret from the related input. If the field is not focused, uses the end
 * of the value so the emoji appends rather than inserting at index 0.
 */
function readSelectionFromInput(
  input: HTMLInputElement | HTMLTextAreaElement | null,
): EmojiSelectionRange | null {
  if (!input) {
    return null;
  }
  const length = input.value.length;
  if (document.activeElement !== input) {
    return { start: length, end: length };
  }
  return {
    start: input.selectionStart ?? length,
    end: input.selectionEnd ?? length,
  };
}

/**
 * Icon button that opens an emoji picker popover and reports the selected emoji via onSelect.
 */
export function EmojiPickerButton({ onSelect, position = 'bottom' }: EmojiPickerButtonProps) {
  const { $t, locale } = useIntl();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Saved before the popover steals focus so insert still targets the original caret.
  const [savedSelection, setSavedSelection] = useState<EmojiSelectionRange | null>(null);
  const popoverOpen = Boolean(anchorEl);
  const { anchorOrigin, transformOrigin } = emojiPickerPlacementByPosition[position];

  /**
   * Preserve the field's selection: without preventDefault the button focuses and
   * the input blurs, clearing selectionStart/selectionEnd before we can read them.
   */
  function handlePickerButtonMouseDown(event: MouseEvent<HTMLElement>) {
    event.preventDefault();
    setSavedSelection(readSelectionFromInput(findRelatedInput(event.currentTarget)));
  }

  function handleOpenPicker(event: MouseEvent<HTMLElement>) {
    // Anchor to the whole field so the picker sits relative to the input, not the icon.
    const fieldRoot = event.currentTarget.closest('.MuiInputBase-root') as HTMLElement | null;
    setAnchorEl(fieldRoot ?? event.currentTarget);
  }

  function handleClosePicker() {
    setAnchorEl(null);
    setSavedSelection(null);
  }

  /** Closes the picker and restores focus + caret to the field that opened it. */
  function handleConfirm() {
    const input = findRelatedInput(anchorEl);
    const selection = savedSelection;
    setAnchorEl(null);
    setSavedSelection(null);
    // Focus after MUI tears down the popover so it does not steal focus back.
    queueMicrotask(() => {
      if (!input) {
        return;
      }
      input.focus();
      if (selection) {
        input.setSelectionRange(selection.start, selection.end);
      }
    });
  }

  function handleSelectEmoji(emojiData: EmojiClickData) {
    const input = findRelatedInput(anchorEl);
    const length = input?.value.length ?? 0;
    const selection = savedSelection ?? { start: length, end: length };
    const emoji = emojiData.emoji;
    // Advance immediately so a second pick in the same session inserts after this emoji.
    const caretAfter = selection.start + emoji.length;
    setSavedSelection({ start: caretAfter, end: caretAfter });
    onSelect(emoji, selection);
  }

  return (
    <>
      <Tooltip title={$t(addEmojiMessage)}>
        <IconButton
          size="small"
          onMouseDown={handlePickerButtonMouseDown}
          onClick={handleOpenPicker}
        >
          <EmojiEmotionsOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={anchorEl}
        open={popoverOpen}
        onClose={handleClosePicker}
        disableRestoreFocus
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <Box
          sx={{
            // Map emoji-picker-react CSS vars to the app palette so the picker
            // matches the platform surfaces instead of the library's built-in theme.
            // `aside.` raises specificity above the library's own var definitions.
            '& aside.EmojiPickerReact': {
              '--epr-bg-color': theme.palette.background.paper,
              '--epr-category-label-bg-color': theme.palette.background.paper,
              '--epr-text-color': theme.palette.text.primary,
              '--epr-search-input-bg-color': theme.palette.background.default,
              '--epr-search-input-bg-color-active': theme.palette.background.default,
              '--epr-search-input-text-color': theme.palette.text.primary,
              '--epr-search-input-placeholder-color': theme.palette.text.secondary,
              '--epr-search-border-color': theme.palette.divider,
              '--epr-search-border-color-active': theme.palette.primary.main,
              '--epr-picker-border-color': theme.palette.divider,
              '--epr-hover-bg-color': theme.palette.action.hover,
              '--epr-focus-bg-color': theme.palette.action.selected,
              '--epr-highlight-color': theme.palette.primary.main,
              '--epr-category-icon-active-color': theme.palette.primary.main,
              border: 'none',
            },
            '& .epr-emoji-category-label': {
              typography: 'subtitle2',
              fontWeight: 'normal',
              height: '20px',
              fontSize: '12px',
            },
          }}
        >
          <EmojiPicker
            onEmojiClick={handleSelectEmoji}
            theme={theme.palette.mode === 'dark' ? Theme.DARK : Theme.LIGHT}
            emojiData={getEmojiPickerData(locale)}
            searchPlaceholder={$t(emojiSearchPlaceholderMessage)}
            skinTonesDisabled
            previewConfig={{
              showPreview: false,
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              px: 1,
              py: 0.75,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Button size="small" variant="contained" onClick={handleConfirm}>
              {$t(emojiPickerOkMessage)}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
